import { useRef, useState } from "react";
import { api, formatErr, zl } from "@/lib/api";


function buildMessage(v, addons, estimate, disc) {
  const date = new Date(v.date + "T12:00").toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
  let m = `Dzień dobry, nazywam się ${v.name}.\nChcę zapytać o dekorację: ${v.occasion}.\n\nData przyjazdu: ${date}\nObiekt: ${v.place}\nBudżet: ${v.budget}`;
  if (v.phone) m += `\nTelefon: ${v.phone}`;
  if (addons.length) m += `\nDodatki: ${addons.join(", ")}`;
  if (disc) m += `\nKod rabatowy: ${disc.code} (−${zl(disc.amount)})`;
  if (estimate) m += `\nOrientacyjny koszt: od ${zl(estimate - (disc?.amount || 0))}`;
  if (v.details) m += `\n\n${v.details}`;
  return m + "\n\nPozdrawiam";
}

const Channels = ({ k }) => (
  <div className="channels">
    <div className="channel"><span>Telefon i WhatsApp</span><b data-testid="contact-phone">{k.phone}</b></div>
    <div className="channel"><span>E-mail</span><b data-testid="contact-email">{k.email}</b></div>
    <div className="channel"><span>Instagram</span><b data-testid="contact-instagram">{k.instagram}</b></div>
  </div>
);

const Result = ({ text }) => {
  const [label, setLabel] = useState("Kopiuj treść");
  const pre = useRef(null);
  const copy = () => navigator.clipboard.writeText(text).then(() => setLabel("Skopiowano"), () => {
    const r = document.createRange(); r.selectNodeContents(pre.current); const s = getSelection(); s.removeAllRanges(); s.addRange(r); setLabel("Zaznaczono, skopiuj ręcznie");
  });
  return (
    <div className="result" data-testid="inquiry-result">
      <p className="label">Dziękujemy · zapytanie wysłane</p>
      <p data-testid="inquiry-result-thanks">Otrzymaliśmy Twoje zapytanie i odpowiemy w ciągu 24 godzin. Poniżej jego podsumowanie.</p>
      <pre ref={pre} data-testid="inquiry-result-text">{text}</pre>
      <div className="row">
        <button className="btn line" type="button" onClick={copy} data-testid="copy-message-btn">{label}</button>
      </div>
    </div>
  );
};

function calcEstimate(c, occasion, addons) {
  const pack = c.packs.items.find((p) => p.occasion === occasion);
  const addonSum = c.addons.items.filter((a) => addons.includes(a.name)).reduce((s, a) => s + Number(a.price || 0), 0);
  return { pack, addonSum, estimate: (pack ? Number(pack.price || 0) : 0) + addonSum };
}

const discountValue = (d, total) => (!d ? 0 : d.kind === "percent" ? Math.round((total * d.value) / 100) : Math.min(d.value, total));

const DiscountField = ({ applied, setApplied }) => {
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/discounts/check", { code }); setApplied(data); setMsg(""); }
    catch (e) { setApplied(null); setMsg(formatErr(e)); } finally { setBusy(false); }
  };
  if (applied) return (
    <div className="field full"><span className="legend">Kod rabatowy</span>
      <p className="disc-ok" data-testid="discount-applied">Kod <b>{applied.code}</b> · {applied.kind === "percent" ? `−${applied.value}%` : `−${zl(applied.value)}`}
        <button type="button" className="link-btn" onClick={() => { setApplied(null); setCode(""); }} data-testid="discount-remove-btn">usuń</button></p></div>
  );
  return (
    <div className="field full"><label htmlFor="kod">Kod rabatowy (opcjonalnie)</label>
      <div className="disc-row">
        <input id="kod" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), apply())} placeholder="np. ROCZNICA10" data-testid="discount-input" />
        <button type="button" className="btn line" disabled={busy} onClick={apply} data-testid="discount-apply-btn">{busy ? "…" : "Zastosuj"}</button>
      </div>
      {msg && <small className="date-warn" data-testid="discount-error">{msg}</small>}
    </div>
  );
};

function validate(v, date, busyDay) {
  const miss = [];
  if (!v.name.trim()) miss.push("imię");
  if (!date) miss.push("datę przyjazdu");
  if (!v.place.trim()) miss.push("obiekt");
  if (miss.length) return `Uzupełnij: ${miss.join(", ")}.`;
  return busyDay ? "Ten termin jest już zajęty. Wybierz inną datę." : "";
}

export const Contact = ({ c, occasion, setOccasion, addons, onToggle, date, setDate, blocked }) => {
  const k = c.contact;
  const [v, setV] = useState({ name: "", phone: "", budget: k.budgets[1] || k.budgets[0] || "", place: "", details: "" });
  const [err, setErr] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [disc, setDisc] = useState(null);
  const [sent, setSent] = useState(0);
  const outRef = useRef(null);
  const set = (key) => (e) => setV({ ...v, [key]: e.target.value });

  const occasions = [...c.packs.items.map((p) => p.occasion), k.other_occasion];
  const { pack, addonSum, estimate } = calcEstimate(c, occasion, addons);
  const busyDay = blocked.includes(date);
  const discAmount = discountValue(disc, estimate);

  const submit = async (e) => {
    e.preventDefault();
    const problem = validate(v, date, busyDay);
    if (problem) { setErr(problem); setOut(""); return; }
    setErr(""); setOut(""); setBusy(true);
    const full = { ...v, date, name: v.name.trim(), place: v.place.trim(), details: v.details.trim(), phone: v.phone.trim(), occasion };
    const message = buildMessage(full, addons, estimate, disc && { code: disc.code, amount: discAmount });
    try {
      await api.post("/inquiries", { ...full, addons, estimate, message, discount_code: disc?.code || "" });
      setOut(message);
      setV({ ...v, name: "", phone: "", place: "", details: "" });
      setDisc(null);
      setSent((n) => n + 1);
      setTimeout(() => outRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    } catch (e2) {
      const m = formatErr(e2);
      if (/kod/i.test(m)) setDisc(null);
      setErr(m);
    } finally { setBusy(false); }
  };

  return (
    <section id="kontakt" data-testid="contact-section">
      <div className="contact">
        <div className="side"><p className="label">{k.label}</p><h2>{k.title}</h2><p>{k.text}</p><Channels k={k} /></div>
        <form onSubmit={submit} noValidate data-testid="inquiry-form">
          <div className="field full">
            <span className="legend">Okazja</span>
            <div className="chips" role="radiogroup">
              {occasions.map((o, i) => (
                <span key={o}><input type="radio" name="okazja" id={`o${i}`} value={o} checked={occasion === o} onChange={() => setOccasion(o)} /><label htmlFor={`o${i}`} data-testid={`occasion-chip-${i}`}>{o}</label></span>
              ))}
            </div>
          </div>
          <div className="field"><label htmlFor="imie">Imię</label><input id="imie" autoComplete="given-name" placeholder="Anna" value={v.name} onChange={set("name")} data-testid="inquiry-name-input" /></div>
          <div className="field"><label htmlFor="tel">Telefon</label><input id="tel" type="tel" autoComplete="tel" placeholder="+48 600 000 000" value={v.phone} onChange={set("phone")} data-testid="inquiry-phone-input" /></div>
          <div className="field"><label htmlFor="data">Data przyjazdu</label><input id="data" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="inquiry-date-input" />{busyDay && <small className="date-warn" data-testid="inquiry-date-busy">Ten dzień jest już zajęty</small>}</div>
          <div className="field"><label htmlFor="budzet">Budżet</label>
            <select id="budzet" value={v.budget} onChange={set("budget")} data-testid="inquiry-budget-select">{k.budgets.map((b) => <option key={b}>{b}</option>)}</select></div>
          <div className="field full"><label htmlFor="miejsce">Obiekt</label><input id="miejsce" placeholder="Nazwa domku lub link do ogłoszenia" value={v.place} onChange={set("place")} data-testid="inquiry-place-input" /></div>
          <div className="field full">
            <span className="legend">Dodatki</span>
            <div className="chips">
              {c.addons.items.map((a, i) => (
                <span key={a.name}><input type="checkbox" id={`a${i}`} checked={addons.includes(a.name)} onChange={() => onToggle(a.name)} /><label htmlFor={`a${i}`} data-testid={`form-addon-chip-${i}`}>{a.name} · {zl(a.price)}</label></span>
              ))}
            </div>
          </div>
          <div className="field full"><label htmlFor="msg">Szczegóły</label><textarea id="msg" placeholder="Kolorystyka, imię solenizanta, godzina check-in" value={v.details} onChange={set("details")} data-testid="inquiry-details-input" /></div>
          <DiscountField key={sent} applied={disc} setApplied={setDisc} />
          <div className="field full estimate" data-testid="inquiry-estimate">
            <span className="legend">Orientacyjny koszt</span>
            <b>{discAmount > 0 && <s data-testid="estimate-before">{zl(estimate)}</s>}od <span data-testid="estimate-value">{zl(estimate - discAmount)}</span></b>
            <small>{pack ? `${pack.title} ${zl(pack.price)}` : "Pakiet do wyceny"}{addonSum ? ` + dodatki ${zl(addonSum)}` : ""}{discAmount > 0 ? ` − rabat ${zl(discAmount)}` : ""}. Dojazd powyżej 30 km wyceniamy osobno.</small>
          </div>
          {err && <p className="err" data-testid="inquiry-error">{err}</p>}
          <div className="field full"><button className="btn solid" type="submit" disabled={busy} style={{ justifySelf: "start" }} data-testid="inquiry-submit-btn">{busy ? "Zapisywanie…" : k.submit}</button></div>
          <div ref={outRef} style={{ gridColumn: "1/-1" }}>{out && <Result text={out} />}</div>
        </form>
      </div>
    </section>
  );
};
